import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserPlanInfo, PlanUsage } from '../types';
import * as subscriptionService from '../services/subscription';

interface SubscriptionState {
  planInfo: UserPlanInfo | null;
  isLoading: boolean;

  // Actions
  fetchPlanInfo: () => Promise<void>;
  setPlanInfo: (planInfo: UserPlanInfo | null) => void;
  clearPlanInfo: () => void;

  // Helpers
  isPremiumOrAbove: () => boolean;
  isDuo: () => boolean;
  canCreate: (resource: keyof PlanUsage) => boolean;
  isMonthRestricted: (month: number, year: number) => boolean;
  isWeekRestricted: (weekStart: Date) => boolean;
  isYearRestricted: (year: number) => boolean;
}

export const useSubscriptionStore = create<SubscriptionState>()(
  persist(
    (set, get) => ({
      planInfo: null,
      isLoading: false,

      fetchPlanInfo: async () => {
        set({ isLoading: true });
        try {
          const response = await subscriptionService.getPlanInfo();
          if (response.success && response.data) {
            set({ planInfo: response.data });
          }
        } catch {
          // Silently fail — plan info will be fetched on next page load
        } finally {
          set({ isLoading: false });
        }
      },

      setPlanInfo: (planInfo) => set({ planInfo }),

      clearPlanInfo: () => set({ planInfo: null }),

      isPremiumOrAbove: () => {
        const { planInfo } = get();
        return planInfo?.effectivePlan === 'premium' || planInfo?.effectivePlan === 'duo';
      },

      isDuo: () => {
        const { planInfo } = get();
        return planInfo?.effectivePlan === 'duo';
      },

      canCreate: (resource) => {
        const { planInfo } = get();
        if (!planInfo) return true; // Permissif par défaut si pas encore chargé
        const limit = planInfo.limits[resource];
        const current = planInfo.usage[resource];
        return limit === -1 || current < limit; // -1 = illimité
      },

      isMonthRestricted: (month, year) => {
        const { planInfo } = get();
        if (!planInfo) return false;
        const limit = planInfo.limits.historyMonths;
        if (limit === -1 || limit === Infinity) return false;
        const now = new Date();
        const monthsDiff = (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - month);
        return monthsDiff >= limit;
      },

      isWeekRestricted: (weekStart) => {
        const { planInfo } = get();
        if (!planInfo) return false;
        const limit = planInfo.limits.historyMonths;
        if (limit === -1 || limit === Infinity) return false;
        const now = new Date();
        const monthsDiff = (now.getFullYear() - weekStart.getFullYear()) * 12 + (now.getMonth() + 1 - (weekStart.getMonth() + 1));
        return monthsDiff >= limit;
      },

      isYearRestricted: (year) => {
        const { planInfo } = get();
        if (!planInfo) return false;
        const limit = planInfo.limits.historyMonths;
        if (limit === -1 || limit === Infinity) return false;
        const now = new Date();
        // En mode annuel, on vérifie si janvier de l'année demandée est hors limite
        const monthsDiff = (now.getFullYear() - year) * 12 + (now.getMonth() + 1 - 1);
        return monthsDiff >= limit;
      },
    }),
    {
      name: 'splinity-subscription',
      partialize: (state) => ({
        planInfo: state.planInfo,
      }),
    },
  ),
);
